import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const SuppliesOverviewPage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState(new URLSearchParams(window.location.search).get('view') === 'reorder' ? 'reorder' : 'horses');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [added, setAdded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const hr = await api.get('/horses');
        const horses = Array.isArray(hr.data) ? hr.data : (hr.data.horses || []);
        const results = await Promise.all(horses.map(async (h) => {
          try {
            const r = await api.get('/supplies/horse/' + h.id);
            const items = Array.isArray(r.data) ? r.data : (r.data.supplies || []);
            return { horse: h, items };
          } catch (e) { return { horse: h, items: [] }; }
        }));
        setGroups(results);
      } catch (e) { setError('Could not load supplies'); }
      setLoading(false);
    })();
  }, []);

  const total = groups.reduce((n, g) => n + g.items.length, 0);

  const all = groups.flatMap(g => g.items.map(it => ({ ...it, horse: g.horse })));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const soonCutoff = new Date(today.getTime() + 14 * 86400000);
  const dueDate = it => it.next_reorder_date ? new Date(it.next_reorder_date + 'T00:00:00') : null;

  const firstOrder = all.filter(it => !it.last_reordered_date && !dueDate(it));
  const withCycle = all.filter(it => dueDate(it));
  const dueNow = withCycle.filter(it => dueDate(it) <= today);
  const dueSoon = withCycle.filter(it => dueDate(it) > today && dueDate(it) <= soonCutoff);
  const later = withCycle.filter(it => dueDate(it) > soonCutoff);

  const addAll = async (items) => {
    let ok = 0;
    for (const it of items) {
      try {
        await api.post('/shopping/list/items', { product_name: it.name, source: 'manual', quantity: 1 });
        ok += 1;
        setAdded(prev => ({ ...prev, [it.id]: true }));
      } catch {}
    }
    if (ok > 0) toast.success(ok + (ok === 1 ? ' item' : ' items') + ' added to your Shop list');
    else toast.error('Failed to add items');
  };

  const fmtDue = it => {
    const d = dueDate(it);
    if (!d) return '';
    const days = Math.round((d - today) / 86400000);
    if (days <= 0) return 'Due now';
    if (days === 1) return 'Due tomorrow';
    return 'Due in ' + days + ' days';
  };

  const Bucket = ({ title, items, accent }) => {
    if (items.length === 0) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <div className="section-label">{title} ({items.length})</div>
        <div className="card" style={{ padding: '4px 14px' }}>
          {items.map(it => (
            <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{it.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {[it.horse.name || it.horse.barn_name, fmtDue(it)].filter(Boolean).join(' · ')}
                </div>
              </div>
              {added[it.id]
                ? <span className="badge badge-green" style={{ fontSize: 10 }}>On list</span>
                : <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}
                    onClick={() => addAll([it])}>+ Add now</button>}
            </div>
          ))}
          <button className={accent ? 'btn btn-primary btn-full' : 'btn btn-secondary btn-full'} style={{ margin: '10px 0' }}
            onClick={() => addAll(items)}>
            🛒 Add all to Shop list
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <TopBar title="Supplies" subtitle="All horses" />

      <div className="tab-bar">
        <div className={'tab-item ' + (view === 'horses' ? 'active' : '')} onClick={() => setView('horses')}>🐴 By horse</div>
        <div className={'tab-item ' + (view === 'reorder' ? 'active' : '')} onClick={() => setView('reorder')}>
          🛒 Reorder{(dueNow.length + firstOrder.length) > 0 ? ' (' + (dueNow.length + firstOrder.length) + ')' : ''}
        </div>
      </div>

      <div className="page-content">
        {loading && <div className="loading-screen"><div className="spinner" /></div>}
        {error && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{error}</p>}

        {!loading && !error && view === 'horses' && (
          <>
            {total === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">🧴</div>
                <h3>No supplies yet</h3>
                <p>Use the button under each horse to add supplies from the catalog</p>
              </div>
            )}
            {groups.map((g) => (
              <div key={g.horse.id} style={{ marginBottom: 12 }}>
                <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => navigate('/horses/' + g.horse.id + '/supplies')}>
                  <span>{g.horse.name || g.horse.barn_name || 'Horse'}</span>
                  <span style={{ fontWeight: 400, textTransform: 'none' }}>
                    {g.items.length} {g.items.length === 1 ? 'supply' : 'supplies'} ›
                  </span>
                </div>
                <div className="card" style={{ padding: '4px 14px' }}>
                  {g.items.length === 0 && (
                    <button className="btn btn-secondary btn-full" style={{ margin: '10px 0' }}
                      onClick={() => navigate('/horses/' + g.horse.id + '/supplies')}>
                      + Add supplies for {g.horse.name || 'this horse'}
                    </button>
                  )}
                  {g.items.map((it) => (
                    <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{it.name}</div>
                        {(it.dose || it.frequency || it.subcategory) && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {[it.subcategory, it.dose, it.frequency].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                      {it.reorder_enabled && <span className="badge badge-green" style={{ fontSize: 10 }}>Reorder on</span>}
                    </div>
                  ))}
                  {g.items.length > 0 && (
                    <button className="btn btn-secondary btn-full" style={{ margin: '10px 0' }}
                      onClick={() => navigate('/horses/' + g.horse.id + '/supplies')}>
                      Manage {g.horse.name || 'horse'} supplies
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {!loading && !error && view === 'reorder' && (
          <>
            {(firstOrder.length + withCycle.length) === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">🛒</div>
                <h3>Nothing to order</h3>
                <p>Supplies with reorder schedules will show up here as they come due</p>
              </div>
            )}
            <Bucket title="Needs first order" items={firstOrder} accent />
            <Bucket title="Due now" items={dueNow} accent />
            <Bucket title="Due within 2 weeks" items={dueSoon} />
            <Bucket title="Scheduled later" items={later} />
            {(firstOrder.length + withCycle.length) > 0 && (
              <button className="btn btn-secondary btn-full" onClick={() => navigate('/shop')}>
                Go to Shop list →
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SuppliesOverviewPage;
